// PageHero · the SINGLE hero used on every page (home + inner).
//
// Capabilities
// ------------
//  • Real horizontal carousel: slides are laid out side-by-side in a
//    flex strip that's `total * containerWidth` px wide. The strip
//    translates left/right; user can drag the strip directly with
//    mouse or touch and watch the next slide come in from the side
//    (Moneta-style). On release the strip snaps to whichever slide
//    is past the 25%-of-viewport-or-velocity-300px/s threshold.
//  • Two-column layout per slide: text + CTAs on the left, animated
//    HeroArtwork on the right (~40% width on lg+). Mobile/tablet
//    stacks artwork above the text, smaller scale.
//  • Per-slide 4-card "floor" sits BELOW the 2-col block, full-width,
//    so it shifts together with its slide. Each floor card has a
//    diagonal hover shine-sweep (premium feel).
//  • Per-slide accent theming · accentColor / accentBg / accentBgDeep /
//    accentInk flow into the pill, the headline gradient, the primary
//    CTA, the active dot fill, the artwork accent, the orb tint.
//  • Constant hero height: the flex carousel uses default `align-items:
//    stretch`, so every slide takes the same height (= max intrinsic
//    height of any slide). Height is structurally locked.
//  • Keyboard ←/→, hover/touch pause, reduced-motion honoured.
//  • Single-slide fallback (no dots, no drag) for inner pages.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, animate as fmAnimate } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, ChevronRight,
  ShieldCheck, Gauge, Zap, Layers,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import HeroArtwork from "@/components/HeroArtwork";

const ROTATE_MS = 6000;
const SWIPE_RATIO = 0.18;       // drag ≥ 18% of viewport → commit
const SWIPE_VELOCITY = 300;     // px/s flick threshold
const DRAG_ACTIVATION_PX = 6;   // ignore micro-jitter before engaging drag

/* ===== Default 4 brand pillars (used when a slide omits .features) ===== */
const DEFAULT_FEATURES = [
  { Icon: ShieldCheck, labelKey: "hero.features.ndd.label",         descKey: "hero.features.ndd.desc",         fb: ["Direct Market Access",     "Orders route straight to liquidity providers · no intermediation."] },
  { Icon: Gauge,       labelKey: "hero.features.speed.label",       descKey: "hero.features.speed.desc",       fb: ["Execution speed",     "Sub-millisecond order routing · co-located in Equinix LD4."] },
  { Icon: Zap,         labelKey: "hero.features.fast.label",        descKey: "hero.features.fast.desc",        fb: ["Fast Transactions",   "Local funding methods · same-day deposits."] },
  { Icon: Layers,      labelKey: "hero.features.instruments.label", descKey: "hero.features.instruments.desc", fb: ["1200+ Instruments", "Forex, metals, indices, crypto, shares · one login."] },
];

/* Breadcrumb */
function Breadcrumb({ items }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="mb-6 flex justify-center" data-testid="page-hero-breadcrumb">
      <ol className="flex items-center flex-wrap gap-1.5 font-mono text-[12px] uppercase tracking-[0.18em] text-text-3">
        <li><Link to="/" className="hover:text-brand transition-colors">{t("common.home", "Home")}</Link></li>
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <ChevronRight size={10} className="opacity-50" />
            {it.to
              ? <Link to={it.to} className="hover:text-brand transition-colors">{it.label}</Link>
              : <span className="text-text-2">{it.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* Floor feature card · institutional broker aesthetic
 * — Left-aligned content (no centered "marketing" look)
 * — Square icon chip with subtle brand-tinted background (no rotation
 *   animation; team feedback flagged the scale+rotate as "bad")
 * — Numeric index in the corner gives the row a deliberate, ordered feel
 * — Hover: small lift + an accent strip slides in along the bottom edge
 * — Shine-sweep retained for premium hover feedback, kept subtle */
function FeatureCard({ feature, accent, delay }) {
  const label = feature.label ?? feature.fb?.[0] ?? "";
  const desc  = feature.desc  ?? feature.fb?.[1] ?? "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3 }}
      className="card-beam shine-sweep relative overflow-hidden p-5 sm:p-6 flex flex-col text-center sm:text-left items-center sm:items-start group"
      style={{ "--shine-color": accent }}
      data-testid={`page-hero-feature-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {/* Icon chip · subtle accent-tinted square (no animation) */}
      <div
        className="w-11 h-11 mb-5 grid place-items-center rounded-xl"
        style={{
          background: `color-mix(in srgb, ${accent} 10%, transparent)`,
          border: `1px solid color-mix(in srgb, ${accent} 18%, transparent)`,
        }}
      >
        <feature.Icon size={20} strokeWidth={1.7} style={{ color: accent }} />
      </div>

      <div className="font-display text-[15px] sm:text-[15.5px] font-medium leading-tight text-text-1 mb-1.5">{label}</div>
      <div className="text-text-3 text-[12px] sm:text-[12.5px] font-light leading-relaxed">{desc}</div>

      {/* Bottom accent strip · slides in on hover (institutional polish,
          no neon glow — kept under 35% opacity per brand guidelines) */}
      <span
        aria-hidden
        className="absolute left-0 bottom-0 h-px w-0 transition-[width] duration-500 ease-out group-hover:w-full"
        style={{ background: `linear-gradient(90deg, ${accent}, transparent)`, opacity: 0.55 }}
      />
    </motion.div>
  );
}

/* One slide · text-left + artwork-right (desktop) + floor below */
function SlideContent({ slide, active, showFeatures, t, idx }) {
  const PillIcon = slide.pill?.Icon;
  const accent      = slide.accentColor   || "var(--brand)";
  const accentBg    = slide.accentBg      || "var(--brand)";
  const accentBgDeep = slide.accentBgDeep || "var(--brand-deep)";
  const accentInk   = slide.accentInk     || "var(--brand-ink)";
  // When the slide has no companion artwork we use a single-column
  // centred layout instead of leaving an empty right column. This is
  // the layout most inner pages get and matches the original brief
  // ("centered like it was with my originals").
  const hasArtwork = !!slide.artwork;

  const features = (slide.features && slide.features.length > 0)
    ? slide.features
    : DEFAULT_FEATURES.map((f) => ({
        ...f,
        label: t(f.labelKey, f.fb[0]),
        desc:  t(f.descKey,  f.fb[1]),
      }));

  return (
    <div
      className="w-full"
      aria-hidden={!active}
      // Pointer-events only on the active slide so background slides
      // can't capture clicks while peeking in during a drag.
      style={{ pointerEvents: active ? "auto" : "none" }}
    >
      {/* Two-column when artwork present · single-column centred otherwise.
          When artwork is set we align both columns to the TOP of the row so
          the phone/visual sits next to the eyebrow + headline instead of
          sliding to the vertical centre when one column is taller. */}
      <div className={hasArtwork ? "grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:items-start" : "max-w-3xl mx-auto"}>
        {/* Text column. When the artwork is a phone (social) the phone
            visually starts ~50px below the column top because of its
            bezel + status bar — push the text down by ~50px so the
            headline lines up with the phone's content area rather
            than sitting awkwardly above it. */}
        <div className={`${hasArtwork ? "text-center lg:text-left" : "text-center"}${slide.artwork === "social" ? " lg:pt-14" : ""}`}>
          {slide.pill && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={active ? { opacity: 1, y: 0 } : { opacity: 0.7, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="pill-mono inline-flex mb-7 items-center"
              data-testid="page-hero-pill"
              style={slide.accentColor ? {
                color: accent,
                borderColor: `color-mix(in srgb, ${accent} 40%, transparent)`,
                background: `color-mix(in srgb, ${accent} 8%, transparent)`,
              } : undefined}
            >
              {PillIcon && <PillIcon size={11} className="mr-1.5 -ml-1" />}
              {slide.pill.text || slide.pill}
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={active ? { opacity: 1, y: 0 } : { opacity: 0.8, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            data-testid="page-hero-title"
            className={`font-display text-4xl/[1.3] sm:text-5xl/[1.3] lg:text-6xl/[1.3] xl:text-7xl/[1.3] font-medium tracking-[-0.025em] rtl:tracking-normal break-words mb-7 ${hasArtwork ? "max-w-2xl mx-auto lg:mx-0" : "max-w-3xl mx-auto"}`}
          >
            {slide.title}
            {slide.accent && (
              <>
                {" "}
                <span
                  className={slide.accentColor ? "font-semibold inline-block" : "text-gradient-brand font-semibold inline-block"}
                  style={slide.accentColor ? {
                    background: `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 60%, #fff) 100%)`,
                    WebkitBackgroundClip: "text", backgroundClip: "text",
                    color: "transparent",
                  } : undefined}
                >
                  {slide.accent}
                </span>
              </>
            )}
          </motion.h1>

          {slide.subline && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={active ? { opacity: 1, y: 0 } : { opacity: 0.7, y: 0 }}
              transition={{ duration: 0.55, delay: 0.18 }}
              data-testid="page-hero-subline"
              className={`text-[color:var(--text-2)] text-base sm:text-lg mb-9 leading-relaxed font-light ${hasArtwork ? "max-w-xl mx-auto lg:mx-0" : "max-w-2xl mx-auto"}`}
            >
              {slide.subline}
            </motion.p>
          )}

          {(slide.primary || slide.secondary) && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={active ? { opacity: 1, y: 0 } : { opacity: 0.8, y: 0 }}
              transition={{ duration: 0.45, delay: 0.26 }}
              className={`flex flex-col sm:flex-row items-center gap-3 ${hasArtwork ? "justify-center lg:justify-start" : "justify-center"}`}
              data-testid="page-hero-ctas"
            >
              {slide.primary && (
                slide.primary.external ? (
                  <a
                    href={slide.primary.href}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-gloss inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl text-[14px] font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${accentBg} 0%, ${accentBgDeep} 100%)`,
                      color: accentInk,
                      minWidth: 200,
                      "--gloss-accent": accentBgDeep,
                    }}
                    data-testid="page-hero-cta-primary"
                  >
                    {slide.primary.label} <ArrowUpRight size={14} />
                  </a>
                ) : (slide.primary.href && slide.primary.href.startsWith("#")) ? (
                  /* Hash-anchor (e.g. `#open-signup`) · render a plain
                     <a> so the global SignupModalProvider click listener
                     fires. <Link to="#…"> would normalise away the hash
                     and route to "/" because react-router treats hash-
                     only `to` props as the current root. */
                  <a
                    href={slide.primary.href}
                    className="btn-gloss inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl text-[14px] font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${accentBg} 0%, ${accentBgDeep} 100%)`,
                      color: accentInk,
                      minWidth: 200,
                      "--gloss-accent": accentBgDeep,
                    }}
                    data-testid="page-hero-cta-primary"
                  >
                    {slide.primary.label} <ArrowRight size={14} />
                  </a>
                ) : (
                  <Link
                    to={slide.primary.to || "#"}
                    className="btn-gloss inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl text-[14px] font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${accentBg} 0%, ${accentBgDeep} 100%)`,
                      color: accentInk,
                      minWidth: 200,
                      "--gloss-accent": accentBgDeep,
                    }}
                    data-testid="page-hero-cta-primary"
                  >
                    {slide.primary.label} <ArrowRight size={14} />
                  </Link>
                )
              )}
              {slide.secondary && (
                <Link
                  to={slide.secondary.to || "#"}
                  className="btn-gloss-ghost inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl text-[14px] font-medium"
                  style={{ color: "var(--text-1)", minWidth: 200 }}
                  data-testid="page-hero-cta-secondary"
                >
                  {slide.secondary.label}
                </Link>
              )}
            </motion.div>
          )}
        </div>

        {/* Artwork column · hidden on small screens, visible on lg+ ·
            items-start so the artwork sits at the TOP of the row (next to
            the eyebrow/headline) instead of vertically-centred which buried
            the phone below the fold on shorter text blocks.
            `overflow-x-hidden` clips any 3D-transformed card that escapes
            the stage box horizontally (TierArt's perspective tilt caused
            right-edge bleed onto the navbar/viewport edges). Vertical
            overflow stays visible so soft halos and bottom glows on the
            phone artwork are NOT squared off at the column edge. */}
        {slide.artwork && (
          <div
            className="hidden lg:flex items-start justify-center pt-2"
            data-testid={`page-hero-artwork-${slide.artwork}`}
          >
            <HeroArtwork variant={slide.artwork} accent={accent} />
          </div>
        )}
      </div>

      {/* Floor — single-column on mobile so each card has full width
          (no awkward text wrapping), 2×2 on tablet, 4-up on desktop. */}
      {showFeatures && (
        <div
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5"
          data-testid="page-hero-features"
        >
          {features.slice(0, 4).map((f, i) => (
            <FeatureCard
              key={(f.label ?? f.labelKey ?? i) + "-" + idx}
              feature={f}
              accent={accent}
              delay={active ? 0.35 + i * 0.08 : 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ===== The PageHero itself ===== */
export default function PageHero({
  // Single-slide props
  pill, title, accent, subline, primary, secondary, artwork,
  // Multi-slide cycler
  slides,
  // Common
  breadcrumb,
  showFeatures = true,
  testId,
}) {
  const { t } = useTranslation();

  const rotation = Array.isArray(slides) && slides.length > 0
    ? slides
    : [{ pill, title, accent, subline, primary, secondary, artwork, key: "single" }];

  const total = rotation.length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const stripRef = useRef(null);
  const viewportRef = useRef(null);
  const [vw, setVw] = useState(0); // viewport (visible carousel area) width in px
  const x = useMotionValue(0);     // current strip translateX in px

  /* Measure viewport · ResizeObserver tracks the visible carousel
     area so we can position slides in px (clean, predictable). */
  useLayoutEffect(() => {
    if (!viewportRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setVw(Math.round(entry.contentRect.width));
    });
    ro.observe(viewportRef.current);
    return () => ro.disconnect();
  }, []);

  /* Re-animate strip when idx changes (but not while user is dragging) */
  const draggingRef = useRef(false);
  useEffect(() => {
    if (vw === 0 || draggingRef.current) return;
    // In RTL the flex track is laid out right-to-left, so slide N is at
    // base position `-N * vw`. The active slide must be translated by
    // `+idx * vw` to land at x=0 (visible). LTR is the inverse.
    const isRTL = typeof document !== "undefined" && document.documentElement.dir === "rtl";
    const target = (isRTL ? +1 : -1) * idx * vw;
    const controls = fmAnimate(x, target, {
      type: "spring", stiffness: 260, damping: 32, mass: 0.9,
    });
    return () => controls.stop();
  }, [idx, vw, x]);

  /* Active slide's accent (used for the active dot fill + orb tint) */
  const accentForActive = rotation[idx]?.accentColor || "var(--brand)";

  /* Auto-rotate */
  useEffect(() => {
    if (total <= 1 || paused) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % total), ROTATE_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused, total]);

  /* Keyboard navigation */
  useEffect(() => {
    if (total <= 1) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % total);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total]);

  /* ===== Custom pointer-event drag handler · works with mouse, touch
     AND synthetic events (more reliable than framer-motion's internal
     drag for cross-input testing). Tracks pointer down → move → up,
     writes the live delta straight into the motion value `x` so the
     strip follows the cursor pixel-for-pixel, then on release decides
     whether to commit a slide change or spring back. ===== */
  const dragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startTime: 0,
    baseX: 0,
    movedPx: 0,
  });

  const stopAnyAnimation = () => {
    // framer-motion's animate() returns an AnimationPlaybackControls
    // object — we can stop it by calling x.stop() on the motion value.
    x.stop();
  };

  const onPointerDown = (e) => {
    if (total <= 1) return;
    // Only primary button / single-finger touches.
    if (e.button !== undefined && e.button !== 0) return;
    // Don't initiate drag-capture when the user is pressing an
    // interactive element (link, button, input) — `setPointerCapture`
    // on the strip would otherwise swallow the click event that the
    // anchor needs to navigate. This is why "Claim my bonus", "Open
    // Live Account", "Browse symbols", "Compare accounts" etc all
    // appeared dead on the homepage carousel.
    if (e.target && e.target.closest && e.target.closest("a, button, input, select, textarea")) return;
    stopAnyAnimation();
    draggingRef.current = true;
    dragStateRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startTime: performance.now(),
      baseX: x.get(),
      movedPx: 0,
    };
    setPaused(true);
    // Capture so we keep receiving pointer events even when the
    // cursor leaves the strip element.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };

  const onPointerMove = (e) => {
    const st = dragStateRef.current;
    if (!st.active || e.pointerId !== st.pointerId) return;
    const dx = e.clientX - st.startX;
    st.movedPx = Math.max(st.movedPx, Math.abs(dx));
    // Apply soft elastic constraint when dragging past the edges.
    // In RTL the strip extends to the right (positive x) instead of
    // the left, so swap the min/max constraints.
    const isRTL = typeof document !== "undefined" && document.documentElement.dir === "rtl";
    let target = st.baseX + dx;
    const edge = (total - 1) * vw;
    const minX = isRTL ? 0    : -edge;
    const maxX = isRTL ? edge : 0;
    if (target > maxX) target = maxX + (target - maxX) * 0.3;
    else if (target < minX) target = minX + (target - minX) * 0.3;
    x.set(target);
  };

  const finishDrag = (e) => {
    const st = dragStateRef.current;
    if (!st.active) return;
    const dx = e.clientX - st.startX;
    const dt = Math.max(1, performance.now() - st.startTime);
    const velocity = (dx / dt) * 1000; // px / s
    st.active = false;
    dragStateRef.current.active = false;
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture(st.pointerId); } catch (_) { /* noop */ }
    // Released the pointer so we resume auto-cycle (slightly delayed
    // by the existing onMouseLeave / onTouchEnd handlers).
    setPaused(false);

    const isRTL = typeof document !== "undefined" && document.documentElement.dir === "rtl";
    const sign  = isRTL ? +1 : -1;          // see comment above
    const snap  = sign * idx * vw;          // x position of the current slide

    if (vw === 0 || total <= 1 || st.movedPx < DRAG_ACTIVATION_PX) {
      // Treat as a tap / click — snap back to current slide.
      fmAnimate(x, snap, { type: "spring", stiffness: 320, damping: 34 });
      return;
    }
    const passDistance = Math.abs(dx) >= vw * SWIPE_RATIO;
    const passVelocity = Math.abs(velocity) >= SWIPE_VELOCITY;
    if (!passDistance && !passVelocity) {
      fmAnimate(x, snap, { type: "spring", stiffness: 320, damping: 34 });
      return;
    }
    // Swipe direction → next/prev. In LTR a leftward swipe (dx < 0)
    // reveals the next slide; in RTL a rightward swipe (dx > 0) does.
    const goingForward = isRTL ? dx > 0 : dx < 0;
    const next = goingForward
      ? Math.min(idx + 1, total - 1)
      : Math.max(idx - 1, 0);
    if (next === idx) {
      fmAnimate(x, snap, { type: "spring", stiffness: 320, damping: 34 });
    } else {
      setIdx(next); // useEffect above re-springs to the new slide
    }
  };

  return (
    <section
      data-testid="page-hero"
      data-hero-id={testId || "page-hero"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      className="relative min-h-[88vh] flex items-center justify-center pt-32 pb-16"
    >
      {/* Bottom fade · merges hero into next section */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{ background: "linear-gradient(180deg, transparent 0%, var(--bg) 100%)" }}
      />

      {/* Foreground · centered content */}
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
        <Breadcrumb items={breadcrumb} />

        {/* Carousel viewport · clips the strip horizontally.
            Pauses auto-advance when the pointer hovers or focus
            enters; resumes on leave/blur. */}
        <div
          ref={viewportRef}
          className={`relative ${total > 1 ? "overflow-hidden" : ""}`}
          data-testid="page-hero-stack"
          style={{ cursor: total > 1 ? "grab" : "default" }}
          onMouseEnter={() => total > 1 && setPaused(true)}
          onMouseLeave={() => total > 1 && setPaused(false)}
          onFocus={() => total > 1 && setPaused(true)}
          onBlur={() => total > 1 && setPaused(false)}
        >
          {/* Strip · flex row, width = total * viewport. Drag is
              implemented via raw pointer events for cross-input
              reliability (mouse, touch, stylus, synthetic). */}
          <motion.div
            ref={stripRef}
            className="flex select-none"
            style={{ x, touchAction: "pan-y" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={finishDrag}
            onPointerCancel={finishDrag}
          >
            {rotation.map((s, i) => {
              const isActive = i === idx;
              return (
                <div
                  key={s.key || i}
                  className="shrink-0 transition-opacity duration-300"
                  style={{
                    width: vw || "100%",
                    // Non-active slides fade out so their glows /
                    // gradient artwork can't bleed into the adjacent
                    // active slide during the spring transition. The
                    // dragging state keeps slides at full opacity so
                    // the swipe gesture remains visually responsive.
                    opacity: draggingRef.current || isActive ? 1 : 0,
                    // aria-hidden the non-active slides for screen
                    // readers so they don't announce duplicated
                    // headings.
                  }}
                  aria-hidden={!isActive}
                >
                  {/* No extra inner padding · the outer `.max-w-7xl mx-auto
                      px-5 lg:px-8` container already gives the carousel
                      enough breathing room from the viewport edges AND
                      matches the navbar's edge exactly. The previous
                      `px-3 sm:px-6 lg:px-10` safe-zone added 40px of
                      DOUBLE-PADDING at lg+, pushing every slide's H1
                      40px inside the navbar's left edge (the misaligned
                      hero the user kept flagging). */}
                  <div>
                    <SlideContent
                      slide={s}
                      active={isActive}
                      showFeatures={showFeatures}
                      t={t}
                      idx={idx}
                    />
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* Dot cycler · only when more than one slide */}
        {total > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2.5" data-testid="page-hero-dots">
            {rotation.map((s, i) => (
              <button
                key={s.key || i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1} of ${total}`}
                aria-current={i === idx}
                data-testid={`page-hero-dot-${i}`}
                className="group relative h-1.5 rounded-full overflow-hidden transition-all"
                style={{
                  width: i === idx ? 44 : 12,
                  background: "color-mix(in srgb, var(--text-3) 25%, transparent)",
                }}
              >
                {i === idx && !paused && (
                  <motion.span
                    key={idx}
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{ background: accentForActive }}
                  />
                )}
                {i === idx && paused && (
                  <span className="absolute inset-0 rounded-full" style={{ background: accentForActive }} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
